import {observable, flow, makeObservable} from "mobx";
import { appStore } from "../";
import { EAIClient } from "Services/EAIClient";
import { Dashboard } from "Stores/dashboardStore";

export interface School {
    id: number
    school_id: string
    display_name: string
    school_type: string | null
    contract_type: string | null
    dashboards: Dashboard[]
    max_number_users: number
    school_logo_url?: string
    timezone: string
    deletable: boolean
    ingestible: boolean
    datasources: Datasource[]
    scheduled_ingestions: ScheduledIngestion[]
    university_name: string
    entity_type: string | null
    program_college: string
    subdomain: string | null
    domain_whitelist: string
    program_college_id: string
    enflux_created: string | null
    enflux_updated: string | null
    hubspot_company_id: string | null
    cohort: string | null
    quickbooks_id: number | null
    contract_start_date: string | null
    at_risk_threshold: number
    auth_info: Partial<AuthInfo>
}

export interface AuthInfo {
    ruuid: string
    subdomain: string
    is_sso: boolean
    is_fusionauth: boolean
    connection_string: string
}

interface DefaultResult {
    totalCount: number
}

export interface SchoolsDataQuery {
    data: School[]
    result: DefaultResult
}

export interface DatasourceTemplate {
    datasource_name: string
    settings: Record<string, Record<string, any>>
}

export interface Datasource{
    datasource_name: string
    settings: Record<string, any>
    ingestible: boolean
    refreshable: boolean
    start_date: Date | null
}

export interface ScheduledIngestion {
    id: number
    school_id: string
    ingest_date: Date
    status: string
    comment: string
    created: Date
    updated: Date
    reason: string
    full_ingestion: boolean
}

export class SchoolStore {
    client: EAIClient = new EAIClient();

    @observable isProcessing = false;
    @observable isRefreshingTickets = false;
    @observable schools: School[] = [];
    @observable school: School;
    @observable datasourceTemplates: DatasourceTemplate[] = [];
    @observable isUpdatingDatasources = false;
    @observable isUpdatingScheduledIngestions = false;
    @observable domainWhitelist: string;

    constructor() {
        makeObservable(this);
    }

    @flow
    *fetchSchools(): any {
        this.isProcessing = true;
        try {
            const resp = yield this.client.fetchSchools();
            this.schools = resp.data.data;
            this.schools.forEach(school => {
                if (school.datasources) {
                    school.datasources!.forEach((d: { start_date: string | Date | null }) => {
                        if(d.start_date){
                            const startDate = new Date(d.start_date);
                            d.start_date = new Date(
                                startDate.getUTCFullYear(),
                                startDate.getUTCMonth(),
                                startDate.getUTCDate()
                            );
                        }else{
                            d.start_date = null;
                        }
                    });
                }
            });
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    *fetchSchool(schoolId: string): any {
        this.isProcessing = true;
        try {
            const resp = yield this.client.fetchSchool(schoolId);
            this.school = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    *createSchool(data: Partial<School>): any {
        this.isProcessing = true;
        try {
            yield this.client.createSchool(data);
        } catch(e) {
            appStore.addError(e);
            throw e;
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    *updateSchool(schoolId: string, data: Partial<School>): any {
        this.isProcessing = true;
        try {
            yield this.client.updateSchool(schoolId, data);
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    *deleteSchool(schoolId: string): any {
        this.isProcessing = true;
        try {
            yield this.client.deleteSchool(schoolId);
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    *refreshTickets(schoolId: string): any {
        this.isRefreshingTickets = true;
        try {
            yield this.client.refreshTickets(schoolId);
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isRefreshingTickets = false;
        }
    }

    @flow
    *fetchDatasourceTemplates(): any {
        this.isUpdatingDatasources = true;
        try {
            const resp = yield this.client.fetchDatasourceTemplates();
            this.datasourceTemplates = resp.data.data;
        } catch(e) {
            appStore.addError(e, "Error fetching datasources. Please try again.");
        } finally {
            this.isUpdatingDatasources = false;
        }
    }

    @flow
    *updateDatasource(schoolId: string, datasourceName: string, data: Datasource): any {
        this.isUpdatingDatasources = true;
        try {
            yield this.client.updateDatasource(schoolId, datasourceName, data);
        } catch(e) {
            appStore.addError(e, `Error updating datasource ${datasourceName} for school ${schoolId}. Please try again.`);
        } finally {
            this.isUpdatingDatasources = false;
        }
    }

    @flow
    *deleteDatasource(schoolId: string, datasourceName: string): any {
        this.isUpdatingDatasources = true;
        try {
            yield this.client.deleteDatasource(schoolId, datasourceName);
        } catch(e) {
            appStore.addError(e, `Error deleting datasource ${datasourceName} for school ${schoolId}. Please try again.`);
        } finally {
            this.isUpdatingDatasources = false;
        }
    }

    @flow
    *saveScheduledIngestion(schoolId: string, ingestDate: Date, comment: string, fullIngestion: boolean): any {
        this.isUpdatingScheduledIngestions = true;
        try {
            yield this.client.saveScheduledIngestion(schoolId, ingestDate, comment, fullIngestion);
        } catch (e) {
            appStore.addError(e, `Error saving scheduled ingestion ${comment}. Please try again.`);
        } finally {
            this.isUpdatingScheduledIngestions = false;
        }
    }

    @flow
    *updateScheduledIngestion(schoolId: string, scheduledIngestionId: number, ingestDate: Date, comment: string, fullIngestion: boolean): any {
        this.isUpdatingScheduledIngestions = true;
        try {
            yield this.client.updateScheduledIngestion(schoolId, scheduledIngestionId, ingestDate, comment, fullIngestion);
        } catch (e) {
            appStore.addError(e, `Error saving scheduled ingestion ${comment}. Please try again.`);
        } finally {
            this.isUpdatingScheduledIngestions = false;
        }
    }

    @flow
    *deleteScheduledIngestion(scheduledIngestion: ScheduledIngestion): any {
        this.isUpdatingScheduledIngestions = true;
        try {
            yield this.client.deleteScheduledIngestion(scheduledIngestion.school_id, scheduledIngestion.id);
        } catch (e) {
            appStore.addError(e, `Error deleting scheduled ingestion ${scheduledIngestion.comment}. Please try again.`);
        } finally {
            this.isUpdatingScheduledIngestions = false;
        }
    }

    @flow
    *fetchDomainWhitelist(): any {
        try {
            const resp = yield this.client.getDomainWhitelist();
            this.domainWhitelist = resp.data.data;
        } catch (e) {
            appStore.addError(e, "Error fetching domain whitelist. Please try again.");
        }
    }
}
